import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { PreviousProject } from "@/features/marketplace/services/marketplace-api.service";

interface PreviousProjectsEditorProps {
	value: PreviousProject[];
	onChange: (projects: PreviousProject[]) => void;
	profileType?: "designer" | "contractor";
}

const EMPTY_PROJECT: PreviousProject = {
	title: "",
	description: "",
	location: "",
};

export function PreviousProjectsEditor({
	value,
	onChange,
	profileType: _profileType,
}: PreviousProjectsEditorProps) {
	const addProject = () => {
		onChange([...value, { ...EMPTY_PROJECT, id: crypto.randomUUID() }]);
	};

	const removeProject = (index: number) => {
		onChange(value.filter((_, i) => i !== index));
	};

	const updateProject = (index: number, patch: Partial<PreviousProject>) => {
		onChange(
			value.map((project, i) =>
				i === index ? { ...project, ...patch } : project,
			),
		);
	};

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<Label className="text-base font-semibold">Previous Projects</Label>
				<Button type="button" variant="outline" size="sm" onClick={addProject}>
					<Plus className="mr-2 h-4 w-4" />
					Add Project
				</Button>
			</div>

			{value.length === 0 && (
				<p className="text-sm text-muted-foreground">
					No previous projects added yet.
				</p>
			)}

			{value.map((project, index) => (
				<div
					key={
						project.id ||
						`${project.title}-${project.location || ""}-${project.year || ""}`
					}
					className="space-y-3 rounded-md border p-4"
				>
					<div className="grid grid-cols-1 gap-3 md:grid-cols-2">
						<div className="space-y-1">
							<Label htmlFor={`project-title-${index}`}>Title</Label>
							<Input
								id={`project-title-${index}`}
								value={project.title || ""}
								onChange={(e) =>
									updateProject(index, { title: e.target.value })
								}
								placeholder="Project title"
							/>
						</div>
						<div className="space-y-1">
							<Label htmlFor={`project-location-${index}`}>Location</Label>
							<Input
								id={`project-location-${index}`}
								value={project.location || ""}
								onChange={(e) =>
									updateProject(index, { location: e.target.value })
								}
								placeholder="City / locality"
							/>
						</div>
					</div>

					<div className="space-y-1">
						<Label htmlFor={`project-description-${index}`}>Description</Label>
						<Textarea
							id={`project-description-${index}`}
							value={project.description || ""}
							onChange={(e) =>
								updateProject(index, { description: e.target.value })
							}
							placeholder="Scope, outcome, style, or notable details"
							rows={3}
						/>
					</div>

					<div className="grid grid-cols-1 gap-3 md:grid-cols-3">
						<div className="space-y-1">
							<Label htmlFor={`project-year-${index}`}>Year</Label>
							<Input
								id={`project-year-${index}`}
								type="number"
								value={project.year != null ? String(project.year) : ""}
								onChange={(e) =>
									updateProject(index, {
										year: e.target.value ? Number(e.target.value) : undefined,
									})
								}
								min={1900}
								max={2100}
							/>
						</div>
						<div className="space-y-1">
							<Label htmlFor={`project-budget-min-${index}`}>Budget Min</Label>
							<Input
								id={`project-budget-min-${index}`}
								type="number"
								value={
									project.budgetMin != null ? String(project.budgetMin) : ""
								}
								onChange={(e) =>
									updateProject(index, {
										budgetMin: e.target.value
											? Number(e.target.value)
											: undefined,
									})
								}
								min={0}
							/>
						</div>
						<div className="space-y-1">
							<Label htmlFor={`project-budget-max-${index}`}>Budget Max</Label>
							<Input
								id={`project-budget-max-${index}`}
								type="number"
								value={
									project.budgetMax != null ? String(project.budgetMax) : ""
								}
								onChange={(e) =>
									updateProject(index, {
										budgetMax: e.target.value
											? Number(e.target.value)
											: undefined,
									})
								}
								min={0}
							/>
						</div>
					</div>

					<div className="flex justify-end">
						<Button
							type="button"
							variant="ghost"
							className="text-destructive hover:text-destructive"
							onClick={() => removeProject(index)}
						>
							<Trash2 className="mr-2 h-4 w-4" />
							Remove
						</Button>
					</div>
				</div>
			))}
		</div>
	);
}
